/**
 * `toMatchVisualBaseline` — TestRelic's visual assertion.
 *
 * It does not compare images itself. Playwright already ships a comparator
 * (pixelmatch, or SSIM-CIE94 when asked) and bundles the decoders it needs, so
 * this delegates to `toHaveScreenshot` and adds the two things Playwright does
 * not give a reporter:
 *
 *  1. **A name that survives into the report.** Playwright's attachments are
 *     named after the resolved snapshot path, which carries project and
 *     platform suffixes. The author's own name is recorded alongside.
 *  2. **Evidence for a passing comparison.** Playwright attaches nothing at all
 *     when the images match, so a green visual check is invisible to any
 *     reporter. The baseline is attached here so the report can show what was
 *     actually asserted against, not just what broke.
 *
 * Both ride the same `-expected` / `-actual` / `-diff` attachment convention
 * the reporter already groups by, so nothing downstream needs a second path.
 */

import { expect as playwrightExpect, test as playwrightTest } from '@playwright/test';
import { statSync } from 'node:fs';
import { basename, extname } from 'node:path';
import type { VisualStatus } from '@testrelic/core';
import { VISUAL_META_ATTACHMENT, type VisualMetaRecord } from './visual-capture.js';
import { supportsSnapshotPathOptions, warnSnapshotPathUnsupported } from './snapshot-path-support.js';

/** Options accepted on top of Playwright's own screenshot options. */
export interface VisualBaselineOptions {
  /**
   * Labels recorded with the comparison. Reserved for the cloud baseline store,
   * where they will select which baselines a branch is allowed to promote.
   */
  readonly tags?: readonly string[];
  /** Anything else is forwarded to `toHaveScreenshot` untouched. */
  readonly [key: string]: unknown;
}

interface MatcherResult {
  pass: boolean;
  message: () => string;
  name: string;
}

// SAFETY: Playwright's TestInfo is reached through `test.info()`, which throws
// outside a running test. Only the three members used here are described.
interface MinimalTestInfo {
  attachments: Array<{ name: string; contentType: string; path?: string; body?: Buffer }>;
  /**
   * Playwright records a first-run write here and then RESOLVES the matcher.
   * Read only as a fallback, on a Playwright too old to name the committed
   * file (see `expectedPathOf`).
   */
  errors: ReadonlyArray<{ message?: string }>;
  snapshotPath(name: string, options: { kind: 'screenshot' }): string;
  attach(
    name: string,
    options: { path?: string; body?: Buffer; contentType?: string },
  ): Promise<void>;
}

/** Image extensions Playwright will accept for a screenshot baseline. */
const IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg'];

/**
 * What Playwright records when `toHaveScreenshot` finds no baseline under the
 * default `updateSnapshots: 'missing'`: it writes the actual AS the baseline,
 * pushes this error onto `testInfo.errors`, and then resolves the matcher with
 * `pass: true`. The test fails at the end; the matcher call does not.
 *
 * Only the fallback signal — `testInfo.errors` is shared by every assertion in
 * the test, so two comparisons resolving at once could read each other's.
 */
const MISSING_SNAPSHOT_RE = /A snapshot doesn't exist at /;

/**
 * The verdict for one `toMatchVisualBaseline` call.
 *
 * A written baseline is not a pass, whatever the matcher resolved: nothing was
 * compared. Playwright writes one on a first run (any mode but `'none'`) and
 * rewrites one under `--update-snapshots`, and resolves as a pass both times.
 * The first run of a suite once recorded every one of these as `passed`.
 */
export function decideStatus(o: {
  pass: boolean;
  /** The committed baseline was written or rewritten during this call. */
  wrote: boolean;
  /** A committed baseline existed before this call. */
  existedBefore: boolean;
}): VisualStatus {
  if (o.wrote) return o.existedBefore ? 'updated' : 'new';
  return o.pass ? 'passed' : 'failed';
}

/** Enough of a file to notice that it was rewritten. */
interface FileMark {
  size: number;
  mtimeMs: number;
}

function markOf(path: string | null): FileMark | null {
  if (!path) return null;
  try {
    const st = statSync(path);
    return { size: st.size, mtimeMs: st.mtimeMs };
  } catch {
    return null;
  }
}

/**
 * The committed baseline this call resolves to — the same file Playwright
 * will read, write, or rewrite — or null on a Playwright too old to say.
 */
function expectedPathOf(testInfo: MinimalTestInfo, snapshotName: string): string | null {
  if (!supportsSnapshotPathOptions(testInfo)) return null;
  try {
    return testInfo.snapshotPath(snapshotName, { kind: 'screenshot' });
  } catch {
    return null;
  }
}

/**
 * Give the snapshot name a file extension if the author left it off.
 *
 * `toHaveScreenshot` rejects a bare name outright ("must have '.png'
 * extension"). Requiring the suffix here would be the sort of papercut that
 * makes a wrapper worse than the thing it wraps, so `'home'` and `'home.png'`
 * both work and resolve to the same baseline.
 */
export function normalizeSnapshotName(name: string): string {
  const lower = name.toLowerCase();
  return IMAGE_EXTENSIONS.some((ext) => lower.endsWith(ext)) ? name : `${name}.png`;
}

/** `test.info()` throws when no test is running; a matcher must not. */
function currentTestInfo(): MinimalTestInfo | null {
  try {
    return playwrightTest.info() as unknown as MinimalTestInfo;
  } catch {
    return null;
  }
}

/**
 * The stem Playwright used for the attachments this assertion just produced.
 *
 * Reading it back off `testInfo.attachments` is the only reliable way to learn
 * it: the stem comes from the resolved output path, which the snapshot path
 * template controls, and reversing that template would be guesswork. When the
 * committed file can be named, the `-expected` attachment that points at it is
 * the one that belongs to this call — so two assertions resolving at once
 * cannot borrow each other's stem. Otherwise the first new comparison
 * attachment is taken.
 */
function stemOfNewAttachments(
  attachments: readonly { name: string; path?: string }[],
  addedAfter: number,
  expectedPath: string | null,
): string | null {
  const stemOf = (name: string): string | null =>
    /^(.+)-(expected|actual|diff|previous)\.[A-Za-z0-9]+$/.exec(name)?.[1] ?? null;
  const added = attachments.slice(addedAfter);
  if (expectedPath) {
    for (const a of added) {
      if (a.path === expectedPath && /-expected\.[A-Za-z0-9]+$/.test(a.name)) return stemOf(a.name);
    }
  }
  for (const a of added) {
    const stem = stemOf(a.name);
    if (stem) return stem;
  }
  return null;
}

/**
 * Attach the committed baseline for a comparison that left no evidence — a
 * pass, or a rewrite under `--update-snapshots`, where Playwright writes the
 * file and attaches nothing.
 */
async function attachBaselineCopy(
  testInfo: MinimalTestInfo,
  expectedPath: string | null,
): Promise<string | null> {
  if (expectedPath === null) {
    warnSnapshotPathUnsupported('Attaching the baseline of a passing comparison');
    return null;
  }
  try {
    const ext = extname(expectedPath) || '.png';
    const stem = basename(expectedPath, ext);
    await testInfo.attach(`${stem}-expected${ext}`, { path: expectedPath });
    return stem;
  } catch {
    // No baseline on disk, or an unwritable output dir. The comparison still
    // passed; it simply has no picture to show for it.
    return null;
  }
}

/** Record the author's name and outcome against the stem the reporter will see. */
async function attachMeta(testInfo: MinimalTestInfo, record: VisualMetaRecord): Promise<void> {
  try {
    await testInfo.attach(VISUAL_META_ATTACHMENT, {
      body: Buffer.from(JSON.stringify([record])),
      contentType: 'application/json',
    });
  } catch {
    // Without this the comparison still appears, just under Playwright's own
    // name and inferred status.
  }
}

/**
 * Assert a page or locator against its committed visual baseline.
 *
 * Registered onto `expect` by `./visual`; see that module for the type
 * declaration that makes it visible to TypeScript.
 */
export async function toMatchVisualBaseline(
  this: { isNot?: boolean },
  target: unknown,
  name: string,
  options: VisualBaselineOptions = {},
): Promise<MatcherResult> {
  // `tags` is ours; everything else belongs to Playwright and is forwarded
  // verbatim, so that every screenshot option keeps working here.
  const screenshotOptions: Record<string, unknown> = { ...options };
  delete screenshotOptions.tags;
  const snapshotName = normalizeSnapshotName(name);
  const testInfo = currentTestInfo();
  const attachedBefore = testInfo?.attachments.length ?? 0;
  const errorsBefore = testInfo?.errors?.length ?? 0;
  const expectedPath = testInfo ? expectedPathOf(testInfo, snapshotName) : null;
  const before = markOf(expectedPath);

  let pass = true;
  let message = '';
  try {
    await (
      playwrightExpect(target) as unknown as {
        toHaveScreenshot(n: string, o: Record<string, unknown>): Promise<void>;
      }
    ).toHaveScreenshot(snapshotName, screenshotOptions);
  } catch (error) {
    pass = false;
    message = error instanceof Error ? error.message : String(error);
  }

  if (testInfo) {
    // `pass` is true whenever Playwright wrote the committed file itself — a
    // first run, or `--update-snapshots` — and nothing was compared then. The
    // file is the witness: it is there now and was not, or it is no longer
    // the file it was. Under `updateSnapshots: 'none'` a missing baseline is
    // a plain failure, and nothing is written.
    const after = markOf(expectedPath);
    const wrote =
      expectedPath !== null
        ? after !== null &&
          (before === null || before.size !== after.size || before.mtimeMs !== after.mtimeMs)
        : (testInfo.errors ?? [])
            .slice(errorsBefore)
            .some((e) => MISSING_SNAPSHOT_RE.test(e.message ?? ''));
    const status = decideStatus({ pass, wrote, existedBefore: before !== null });

    // A pass and a rewrite leave no attachment behind, so the committed file
    // is attached here; a first write and a failure are attached by
    // Playwright under its own stem, which is read back rather than guessed.
    const stem =
      status === 'passed' || status === 'updated'
        ? await attachBaselineCopy(testInfo, expectedPath)
        : stemOfNewAttachments(testInfo.attachments, attachedBefore, expectedPath);

    if (stem) {
      await attachMeta(testInfo, {
        stem,
        name,
        status,
        ...(expectedPath ? { committedPath: expectedPath } : {}),
      });
    }
  }

  return {
    pass,
    name: 'toMatchVisualBaseline',
    message: () =>
      pass ? `Expected "${name}" to differ from its visual baseline, but it matched.` : message,
  };
}
